import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  Link,
  Paper,
  Skeleton,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SecurityIcon from "@mui/icons-material/Security";
import StorefrontIcon from "@mui/icons-material/Storefront";
import type { ReactNode } from "react";
import DrawerHeader from "shared-ui/src/components/detail-drawer/DrawerHeader";
import { useGetUserProfileDrawerProfile } from "@features/unified-user-header-feature/hooks/useGetUserProfileDrawerProfile";
import type { CurrentUserDrawerResponse } from "@lib/ShiOneClient";
import { useThemeMode } from "../../../../../../theme/ThemeModeProviderContext";
import lightTheme from "shared-ui/src/theme";
import darkTheme from "shared-ui/src/theme/darkTheme";
import CxEmployeeAvatar, { AvatarSize } from "@components/CxEmployeeAvatar";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { isImpersonatingAccount, isImpersonatingUser } from "shared-ui/src/utils/clientmanagement";

interface UserProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

function getInitials(firstName?: string | null, lastName?: string | null, fullName?: string | null) {
  const first = firstName?.trim()?.[0];
  const last = lastName?.trim()?.[0];
  if (first || last) return `${first ?? ""}${last ?? ""}`.toUpperCase();

  return fullName
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() ?? "";
}

function formatPhone(phoneNumber?: string | null, extension?: string | null) {
  if (!phoneNumber) return "Not provided";
  return extension ? `${phoneNumber} x${extension}` : phoneNumber;
}

function LoadingState() {
  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Skeleton variant="circular" width={84} height={84} />
      <Skeleton width="45%" />
      <Skeleton width="65%" />
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={180} />
    </Stack>
  );
}

function SectionCard({ title, subtitle, children }: Readonly<{ title: string; subtitle?: string; children: ReactNode }>) {
  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body1" fontWeight={500}>{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

function ContactRow({ icon, label, children }: Readonly<{ icon: ReactNode; label: string; children: ReactNode }>) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Box sx={{ color: "text.secondary", display: "flex", pt: 0.25 }}>{icon}</Box>
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.1 }}>
          {label}
        </Typography>
        <Typography component="div" variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

function AccessRow({ icon, title, subtitle }: Readonly<{ icon: ReactNode; title: string; subtitle?: string }>) {
  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1.5,
        gap: 2,
        bgcolor: "background.paper",
        width: "100%",
        borderRadius: 1,
      }}
    >
      <Avatar sx={{ width: 38, height: 38, bgcolor: "action.selected", color: "text.secondary", flexShrink: 0 }}>
        {icon}
      </Avatar>
      <Box minWidth={0} flex={1}>
        <Typography variant="body1" noWrap title={title} sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap title={subtitle} sx={{ display: "block" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function ProfileContent({ profile }: { profile: CurrentUserDrawerResponse }) {
  const initials = getInitials(profile.firstName, profile.lastName, profile.fullName);
  const phone = formatPhone(profile.phoneNumber, profile.phoneExtension);
  const { getClaimValue } = useClaims();
  const isImpersonatingClaim = getClaimValue("isImpersonating")?.toLowerCase() === "true";
  const isImpersonatingView =
    profile.isImpersonating ||
    isImpersonatingClaim ||
    isImpersonatingAccount() ||
    isImpersonatingUser();
  const userEntraId = profile.isShi && !isImpersonatingView ? getClaimValue("userEntraId") : null;
  const roleChipLabel = profile.isShi && !isImpersonatingView && profile.roleDisplayName ? profile.roleDisplayName : null;

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      {/* Identity row — no card border/padding */}
      <Stack direction="row" spacing={2} alignItems="center">
        {userEntraId ? (
          <CxEmployeeAvatar
            entraId={userEntraId}
            displayName={profile.fullName ?? ""}
            photoSize={AvatarSize.xl}
          />
        ) : (
          <Avatar
            src={profile.profilePhotoUrl ?? undefined}
            alt={profile.fullName ?? "User profile"}
            sx={{ width: 84, height: 84, fontSize: 28, fontWeight: 700, flexShrink: 0 }}
          >
            {initials || <PersonIcon />}
          </Avatar>
        )}
        <Box minWidth={0} flex={1}>
          <Typography variant="h6" noWrap title={profile.fullName} sx={{ fontWeight: 700 }}>
            {profile.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap title={profile.email}>
            {profile.email}
          </Typography>
          {roleChipLabel && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip size="small" label={roleChipLabel} />
            </Stack>
          )}
        </Box>
      </Stack>

      <SectionCard title="Contact Information" subtitle="How your organization and SHI can reach you">
        <Card variant="outlined" sx={{ borderRadius: 1, bgcolor: "background.paper", width: "100%" }}>
          <CardContent>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
              <ContactRow icon={<EmailIcon fontSize="small" />} label="Email Address">
                <Link href={`mailto:${profile.email}`}>{profile.email}</Link>
              </ContactRow>
              <ContactRow icon={<PhoneIcon fontSize="small" />} label="Phone Number">
                {phone}
              </ContactRow>
            </Box>
          </CardContent>
        </Card>
      </SectionCard>

      <SectionCard title="Security Groups" subtitle="Data and support permissions assigned to your profile">
        {(profile.securityGroups ?? []).length ? (
          <Stack spacing={1.5}>
            {(profile.securityGroups ?? []).map((group) => (
              <AccessRow
                key={group.id}
                icon={<SecurityIcon fontSize="small" />}
                title={group.name ?? ""}
                subtitle={group.role ? `Role: ${group.role}` : undefined}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No security groups assigned.
          </Typography>
        )}
      </SectionCard>

      <SectionCard title="Storefronts" subtitle="Buying experiences and purchasing permissions">
        {(profile.storefronts ?? []).length ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 1.5,
            }}
          >
            {(profile.storefronts ?? []).map((storefront) => (
              <AccessRow
                key={storefront.id}
                icon={<StorefrontIcon fontSize="small" />}
                title={storefront.name ?? ""}
                subtitle={storefront.role ? `Role: ${storefront.role}` : undefined}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No storefronts assigned.
          </Typography>
        )}
      </SectionCard>
    </Stack>
  );
}

export default function UserProfileDrawer({ open, onClose }: Readonly<UserProfileDrawerProps>) {
  const { data, isLoading, isError, error } = useGetUserProfileDrawerProfile(open);
  const { mode } = useThemeMode();
  const drawerTheme = mode === "light" ? lightTheme : darkTheme;

  return (
    <ThemeProvider theme={drawerTheme}>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      role="dialog"
      aria-label="User Profile"
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            width: { xs: "100vw", sm: "40vw" },
            minWidth: { sm: 360 },
            maxWidth: "100vw",
            overflow: "hidden",
          },
        },
      }}
    >
      <DrawerHeader
        iconElement={
          <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
            <PersonIcon fontSize="small" />
          </Avatar>
        }
        header={data?.fullName ?? "User Profile"}
        subheader="User Profile"
        onClose={onClose}
      />
      <Divider />
      <Box sx={{ flex: 1, width: "100%", overflowY: "auto", bgcolor: "table.header" }}>
        {isLoading && <LoadingState />}
        {isError && (
          <Alert severity="error" sx={{ m: 3 }}>
            {error?.message ?? "Unable to load user profile."}
          </Alert>
        )}
        {!isLoading && !isError && data && <ProfileContent profile={data} />}
      </Box>
    </Drawer>
    </ThemeProvider>
  );
}
